import type { AccessDecision, PortalSnapshotResponse } from "./types.js";

type PortalSnapshot = PortalSnapshotResponse["snapshot"];

export function isOverviewIncrementalRefresh(
  current: PortalSnapshot,
  incoming: PortalSnapshot,
  currentAccess: AccessDecision,
  incomingAccess: AccessDecision
): boolean {
  return JSON.stringify(overviewPageStructure(current)) === JSON.stringify(overviewPageStructure(incoming))
    && JSON.stringify(currentAccess) === JSON.stringify(incomingAccess);
}

export function isGarageContentUnchanged(current: PortalSnapshot, incoming: PortalSnapshot): boolean {
  return JSON.stringify(garagePageState(current)) === JSON.stringify(garagePageState(incoming));
}

export function isSkinsContentUnchanged(
  current: PortalSnapshot,
  incoming: PortalSnapshot,
  currentAccess: AccessDecision,
  incomingAccess: AccessDecision
): boolean {
  return JSON.stringify(skinsPageState(current)) === JSON.stringify(skinsPageState(incoming))
    && JSON.stringify(currentAccess) === JSON.stringify(incomingAccess);
}

export function shouldRefreshPortalLivePage(
  page: string,
  _snapshot: PortalSnapshot
): page is "overview" | "garage" | "skins" {
  return page === "overview" || page === "garage" || page === "skins";
}

export function isLiveSkinEditorSpeciesCurrent(editorSpecies: string | undefined, liveSpecies: string): boolean {
  return Boolean(editorSpecies && normalizeSpecies(editorSpecies) === normalizeSpecies(liveSpecies));
}

function overviewPageStructure(snapshot: PortalSnapshot): unknown {
  // Everything else on the overview has an incremental patch target. In
  // particular, RCON presence, PlayerStats detail, Prime, skin-capture and
  // ledger changes must not disconnect the mounted model/stat card.
  return {
    serverId: snapshot.server.id,
    liveDino: {
      species: normalizeSpecies(snapshot.liveDino.species),
      status: snapshot.liveDino.status.trim().toLowerCase()
    }
  };
}

function garagePageState(snapshot: PortalSnapshot): unknown {
  return {
    garage: snapshot.garage,
    garageCapacity: snapshot.garageCapacity,
    liveDino: {
      status: snapshot.liveDino.status,
      species: snapshot.liveDino.species
    }
  };
}

function skinsPageState(snapshot: PortalSnapshot): unknown {
  return {
    liveDino: {
      uid: snapshot.liveDino.uid,
      status: snapshot.liveDino.status,
      species: snapshot.liveDino.species,
      displayName: snapshot.liveDino.displayName
    },
    skinCapture: snapshot.skinCapture,
    skinPresets: snapshot.skinPresets
  };
}

function normalizeSpecies(value: string): string {
  const leaf = value.trim().split(/[\\/]/).at(-1) ?? value;
  return leaf
    .replace(/^BP_/i, "")
    .replace(/(?:\.[^.]+)?_C$/i, "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();
}
